import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, onSnapshot, doc, updateDoc, getDocs, query, where } from 'firebase/firestore';
import '../css/AgentOutstanding.css';

const AgentOutstanding = () => {
  const [invoices, setInvoices] = useState([]);
  const [filteredInvoices, setFilteredInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Search & Date Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  // Pagination States (15 Rows Per Page)
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 15;

  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingCart, setEditingCart] = useState([]);
  const [isVerifying, setIsVerifying] = useState(false);

  const LOGO_URL = "https://mrwhiteuae.com/static/media/logo.e6e2c6a8470aa9240988.png";

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'agentInvoices'), (snapshot) => {
      const fetchedInvoices = snapshot.docs.map((docSnap) => {
        const data = docSnap.data();

        let calculatedAgentTotal = 0;
        if (Array.isArray(data.cartDetails)) {
          calculatedAgentTotal = data.cartDetails.reduce((sum, item) => {
            const amount = parseFloat(item.agentAmount) || 0;
            return sum + amount;
          }, 0);
        }

        return {
          id: docSnap.id,
          companyName: data.companyName || 'N/A',
          agentName: data.agentName || 'N/A',
          agentPhone: data.agentPhone || data.phone || 'N/A',
          displayOrderId: data.displayOrderId || data.orderId || docSnap.id,
          orderId: data.orderId || data.displayOrderId || docSnap.id,
          originalCollection: data.originalCollection || 'bookings',
          cartDetails: Array.isArray(data.cartDetails) ? data.cartDetails : [],
          invoiceAmount: parseFloat(data.invoiceAmount) || calculatedAgentTotal,
          verificationStatus: data.verificationStatus || 'Pending Verification',
          createdAt: data.createdAt ? (data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt)) : null,
        };
      });

      fetchedInvoices.sort((a, b) => {
        if (!a.createdAt) return 1;
        if (!b.createdAt) return -1;
        return b.createdAt - a.createdAt;
      });

      setInvoices(fetchedInvoices);
      setFilteredInvoices(fetchedInvoices);
      setLoading(false);
    }, (error) => {
      console.error("Error fetching agent invoices: ", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let result = invoices;

    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((item) => {
        const orderIdMatch = (item.displayOrderId || '').toLowerCase().includes(term);
        const companyMatch = (item.companyName || '').toLowerCase().includes(term);
        const agentMatch = (item.agentName || '').toLowerCase().includes(term);
        const phoneMatch = (item.agentPhone || '').toString().includes(term);

        return orderIdMatch || companyMatch || agentMatch || phoneMatch;
      });
    }

    if (fromDate) {
      const start = new Date(fromDate);
      start.setHours(0, 0, 0, 0);
      result = result.filter((item) => item.createdAt && item.createdAt >= start);
    }

    if (toDate) {
      const end = new Date(toDate);
      end.setHours(23, 59, 59, 999);
      result = result.filter((item) => item.createdAt && item.createdAt <= end);
    }

    setFilteredInvoices(result);
    setCurrentPage(1);
  }, [searchTerm, fromDate, toDate, invoices]);

  useEffect(() => {
    if (selectedInvoice) {
      setEditingCart(JSON.parse(JSON.stringify(selectedInvoice.cartDetails || [])));
    }
  }, [selectedInvoice]);

  const totalOutstanding = filteredInvoices.reduce((acc, inv) => acc + inv.invoiceAmount, 0);

  const totalPages = Math.ceil(filteredInvoices.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedInvoices = filteredInvoices.slice(startIndex, startIndex + rowsPerPage);

  // 🎯 Calculate Live Total Agent Amount for Editing Cart in Modal
  const currentModalAgentTotal = editingCart.reduce((sum, item) => {
    return sum + (parseFloat(item.agentAmount) || 0);
  }, 0);

  // 🎯 Recalculate Amount automatically when Quantity/Weight or Unit Price is changed
  const handleItemFieldChange = (index, field, value) => {
    const updated = [...editingCart];
    const targetItem = { ...updated[index], [field]: value };

    const unitPrice = parseFloat(targetItem.unitPrice) || 0;
    const isKg = targetItem.pricingType === 'kg';
    const qtyOrWeight = isKg ? (parseFloat(targetItem.weight) || 0) : (parseFloat(targetItem.quantity) || 0);

    // Auto calculate Amount = Qty/Weight x Unit Price
    if (field === 'quantity' || field === 'weight' || field === 'unitPrice') {
      targetItem.agentAmount = (qtyOrWeight * unitPrice).toFixed(2);
    }

    updated[index] = targetItem;
    setEditingCart(updated);
  };

  // 🎯 ADMIN VERIFY AND SYNC PRICING TO USER APP
  const handleVerifyAndApprove = async () => {
    if (!selectedInvoice) return;
    setIsVerifying(true);

    try {
      let freshAgentTotal = 0;
      let freshUserSubtotal = 0;

      const sanitizedCart = editingCart.map((item) => {
        const agAmt = parseFloat(item.agentAmount) || 0;
        freshAgentTotal += agAmt;

        const qty = parseInt(item.quantity) || 1;
        const weight = parseFloat(item.weight) || 0;
        const rate = parseFloat(item.offerPrice || item.itemRate) || 0;

        let calculatedTotal = 0;
        if (item.pricingType === 'kg' && weight > 0) {
          calculatedTotal = weight * rate;
        } else {
          calculatedTotal = qty * rate;
        }
        freshUserSubtotal += calculatedTotal;

        return {
          ...item,
          agentAmount: agAmt.toFixed(2),
        };
      });

      // 1. Update Agent Invoices Record
      const invRef = doc(db, 'agentInvoices', selectedInvoice.id);
      await updateDoc(invRef, {
        cartDetails: sanitizedCart,
        invoiceAmount: freshAgentTotal.toFixed(2),
        verificationStatus: 'Verified',
        verifiedAt: new Date(),
      });

      // 2. Sync Verified Prices & Cart to User App (orderAcceptedbyAgent & bookings/expressOrder)
      const targetOrderId = selectedInvoice.displayOrderId || selectedInvoice.orderId;

      const agentOrderQuery = query(collection(db, 'orderAcceptedbyAgent'), where('displayOrderId', '==', targetOrderId));
      const agentOrderSnap = await getDocs(agentOrderQuery);

      if (!agentOrderSnap.empty) {
        agentOrderSnap.forEach(async (d) => {
          const docData = d.data();
          const paymentMap = docData.payment || {};
          const delFee = parseFloat(paymentMap.deliveryFee) || 0;
          const tax = parseFloat(paymentMap.tax) || 0;
          const freshTotalPayable = freshUserSubtotal + delFee + tax;

          await updateDoc(doc(db, 'orderAcceptedbyAgent', d.id), {
            cart: sanitizedCart,
            payment: {
              ...paymentMap,
              subtotal: freshUserSubtotal.toFixed(2),
              totalPayable: freshTotalPayable.toFixed(2),
            }
          });
        });
      }

      const collectionName = selectedInvoice.originalCollection || 'bookings';
      const origQuery = query(collection(db, collectionName), where('displayOrderId', '==', targetOrderId));
      const origSnap = await getDocs(origQuery);

      if (!origSnap.empty) {
        origSnap.forEach(async (d) => {
          const docData = d.data();
          const paymentMap = docData.payment || {};
          const delFee = parseFloat(paymentMap.deliveryFee) || 0;
          const tax = parseFloat(paymentMap.tax) || 0;
          const freshTotalPayable = freshUserSubtotal + delFee + tax;

          await updateDoc(doc(db, collectionName, d.id), {
            cart: sanitizedCart,
            payment: {
              ...paymentMap,
              subtotal: freshUserSubtotal.toFixed(2),
              totalPayable: freshTotalPayable.toFixed(2),
            }
          });
        });
      }

      alert("✅ Invoice verified and synced to User App successfully!");
      setSelectedInvoice(null);
      setIsVerifying(false);
    } catch (err) {
      console.error("Error verifying invoice: ", err);
      alert("Failed to verify invoice: " + err.message);
      setIsVerifying(false);
    }
  };

  const handleExportCSV = () => {
    if (filteredInvoices.length === 0) {
      alert("No data available to export!");
      return;
    }

    const headers = ["Order ID", "Company Name", "Agent Name", "Agent Phone", "Items Count", "Invoice Amount (AED)", "Verification Status", "Created Date"];
    const csvRows = [headers.join(",")];

    filteredInvoices.forEach((inv) => {
      const formattedDate = inv.createdAt ? inv.createdAt.toLocaleDateString('en-GB') : 'N/A';
      const row = [
        `"${inv.displayOrderId}"`,
        `"${inv.companyName.replace(/"/g, '""')}"`,
        `"${inv.agentName.replace(/"/g, '""')}"`,
        `"${inv.agentPhone}"`,
        inv.cartDetails.length,
        inv.invoiceAmount.toFixed(2),
        `"${inv.verificationStatus}"`,
        `"${formattedDate}"`
      ];
      csvRows.push(row.join(","));
    });

    const csvContent = "data:text/csv;charset=utf-8," + csvRows.join("\n");
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Agent_Outstanding_Statement_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportSummaryPDF = () => {
    if (filteredInvoices.length === 0) {
      alert("No data available to export!");
      return;
    }

    const printWindow = window.open('', '_blank');
    const rowsHTML = filteredInvoices.map((inv, idx) => {
      const itemsList = inv.cartDetails.map(c => 
        `• ${c.name || c.itemName || 'Item'} [${c.serviceName || 'Wash'}] (${c.quantity ? `${c.quantity} Pcs` : `${c.weight || 1} kg`}): AED ${(parseFloat(c.agentAmount) || 0).toFixed(2)}`
      ).join('<br/>');

      return `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: center; vertical-align: top;">${idx + 1}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-weight: bold; color: #2563eb; vertical-align: top;">${inv.displayOrderId}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; vertical-align: top;">
            <strong>${inv.companyName}</strong><br/>
            <span style="font-size: 11px; color: #64748b;">Agent: ${inv.agentName} (${inv.agentPhone})</span>
          </td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; font-size: 11px; color: #334155; vertical-align: top;">
            ${itemsList || 'No Items'}
          </td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: bold; color: #dc2626; vertical-align: top;">AED ${inv.invoiceAmount.toFixed(2)}</td>
        </tr>
      `;
    }).join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Agent_Outstanding_Summary_Report</title>
          <style>
            body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 25px; color: #1e293b; }
            .header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #2563eb; padding-bottom: 15px; margin-bottom: 20px; }
            .logo { max-height: 60px; }
            table { width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 15px; }
            th { background: #2563eb; color: #ffffff; padding: 10px; text-align: left; font-size: 11px; text-transform: uppercase; }
            .total-box { margin-top: 20px; text-align: right; font-size: 16px; font-weight: bold; color: #1d4ed8; background: #eff6ff; padding: 12px; border-radius: 6px; border: 1px solid #bfdbfe; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${LOGO_URL}" alt="Logo" class="logo" />
            <div style="text-align: right;">
              <h2 style="margin: 0; color: #2563eb;">AGENT INVOICES REPORT</h2>
              <p style="margin: 4px 0 0 0; font-size: 12px; color: #64748b;">Generated On: ${new Date().toLocaleDateString('en-GB')}</p>
            </div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="text-align: center; width: 30px;">#</th>
                <th style="width: 110px;">Order ID</th>
                <th style="width: 220px;">Company & Agent</th>
                <th>Cart Details Breakdown</th>
                <th style="text-align: right; width: 130px;">Amount (AED)</th>
              </tr>
            </thead>
            <tbody>
              ${rowsHTML}
            </tbody>
          </table>
          <div class="total-box">
            Total Invoices Amount: AED ${totalOutstanding.toFixed(2)}
          </div>
          <script>
            window.onload = function() { window.print(); };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleDownloadPDF = (invoice) => {
    const printWindow = window.open('', '_blank');
    const invoiceDate = invoice.createdAt ? invoice.createdAt.toLocaleDateString('en-GB') : new Date().toLocaleDateString('en-GB');

    const cartRowsHTML = invoice.cartDetails.length > 0 ? (
      invoice.cartDetails.map((item, idx) => `
        <tr>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: center;">${idx + 1}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;"><strong>${item.name || item.itemName || 'Item'}</strong></td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0;">${item.serviceName || 'N/A'}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: center;">${item.quantity ? `${item.quantity} Pcs` : `${item.weight || 1} kg`}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: right;">AED ${(parseFloat(item.unitPrice) || 0).toFixed(2)}</td>
          <td style="padding: 10px; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: bold; color: #1e293b;">AED ${(parseFloat(item.agentAmount) || 0).toFixed(2)}</td>
        </tr>
      `).join('')
    ) : (
      `<tr><td colSpan="6" style="text-align:center; padding: 20px; color: #64748b;">No cart items recorded.</td></tr>`
    );

    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Invoice_${invoice.displayOrderId}</title>
          <style>
            body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; padding: 30px; color: #1e293b; }
            .invoice-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #2563eb; padding-bottom: 20px; margin-bottom: 25px; }
            .company-logo { max-height: 70px; }
            .invoice-title { text-align: right; }
            .invoice-title h1 { margin: 0; color: #2563eb; font-size: 24px; text-transform: uppercase; }
            .invoice-title p { margin: 4px 0 0 0; font-size: 13px; color: #64748b; }
            .details-grid { display: flex; justify-content: space-between; background: #f8fafc; padding: 16px; border-radius: 8px; border: 1px solid #e2e8f0; margin-bottom: 25px; }
            .info-block p { margin: 4px 0; font-size: 13px; }
            .info-block strong { color: #0f172a; }
            table { width: 100%; border-collapse: collapse; margin-bottom: 25px; font-size: 13px; }
            th { background: #2563eb; color: #ffffff; padding: 10px; text-align: left; }
            .total-box { text-align: right; background: #eff6ff; padding: 15px; border-radius: 8px; border: 1px solid #bfdbfe; }
            .total-box h3 { margin: 0; color: #1d4ed8; font-size: 18px; }
            .footer-note { text-align: center; margin-top: 40px; font-size: 11px; color: #94a3b8; border-top: 1px solid #e2e8f0; padding-top: 15px; }
          </style>
        </head>
        <body>
          <div class="invoice-header">
            <img src="${LOGO_URL}" alt="Mr White Logo" class="company-logo" />
            <div class="invoice-title">
              <h1>AGENT INVOICE</h1>
              <p>Order ID: <strong>${invoice.displayOrderId}</strong></p>
              <p>Date: ${invoiceDate}</p>
            </div>
          </div>

          <div class="details-grid">
            <div class="info-block">
              <p><strong>Company:</strong> ${invoice.companyName}</p>
              <p><strong>Agent Name:</strong> ${invoice.agentName}</p>
              <p><strong>Phone:</strong> ${invoice.agentPhone}</p>
            </div>
          </div>

          <table>
            <thead>
              <tr>
                <th style="text-align: center; width: 40px;">#</th>
                <th>Item Name</th>
                <th>Service Name</th>
                <th style="text-align: center;">Qty / Weight</th>
                <th style="text-align: right;">Unit Price (AED)</th>
                <th style="text-align: right;">Agent Amount (AED)</th>
              </tr>
            </thead>
            <tbody>
              ${cartRowsHTML}
            </tbody>
          </table>

          <div class="total-box">
            <h3>Total Amount: AED ${invoice.invoiceAmount.toFixed(2)}</h3>
          </div>

          <div class="footer-note">
            <p>This is a computer-generated invoice from Mr White Laundry. No signature required.</p>
          </div>

          <script>
            window.onload = function() {
              window.print();
            };
          </script>
        </body>
      </html>
    `;

    printWindow.document.write(invoiceHTML);
    printWindow.document.close();
  };

  if (loading) {
    return (
      <div className="outstanding-container">
        <div className="admin-loader">Loading Agent Invoices Data...</div>
      </div>
    );
  }

  return (
    <div className="outstanding-container">
      <div className="outstanding-card">
        {/* Header Title & Export Buttons */}
        <div className="outstanding-header">
          <div>
            <h2>Agent Invoices Statements</h2>
            <div className="summary-chip" style={{ marginTop: '8px' }}>
              <span>Total Amount:</span>
              <strong>AED {totalOutstanding.toFixed(2)}</strong>
            </div>
          </div>

          <div className="export-btn-group">
            <button onClick={handleExportCSV} className="btn-export btn-excel">
              📊 Export Excel (CSV)
            </button>
            <button onClick={handleExportSummaryPDF} className="btn-export btn-pdf-summary">
              📄 Export PDF Report
            </button>
          </div>
        </div>

        {/* Search & Date Range Filter Bar */}
        <div className="filter-bar">
          <div className="filter-inputs-group">
            <div className="search-input-wrapper">
              <i className="fas fa-search search-icon"></i>
              <input
                type="text"
                placeholder="Search by Order ID, Company, Agent, Phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              {searchTerm && (
                <button className="clear-search-btn" onClick={() => setSearchTerm('')}>
                  &times;
                </button>
              )}
            </div>

            <div className="date-filter-wrapper">
              <div className="date-input-field">
                <label>From Date:</label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>
              <div className="date-input-field">
                <label>To Date:</label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              {(fromDate || toDate || searchTerm) && (
                <button
                  className="btn-clear-filters"
                  onClick={() => {
                    setSearchTerm('');
                    setFromDate('');
                    setToDate('');
                  }}
                >
                  Clear Filters
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Outstanding Statements Table */}
        <div className="table-wrapper">
          <table className="outstanding-table">
            <thead>
              <tr>
                <th style={{ width: '40px' }}>#</th>
                <th style={{ width: '130px' }}>Order ID</th>
                <th style={{ width: '320px' }}>Company, Agent & Contact</th>
                <th>Items Count</th>
                <th>Invoice Amount (AED)</th>
                <th>Verification Status</th>
                <th style={{ textAlign: 'center', width: '120px' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedInvoices.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: 'center', padding: '32px', color: '#64748b' }}>
                    No agent invoices or outstanding records found.
                  </td>
                </tr>
              ) : (
                paginatedInvoices.map((invoice, index) => (
                  <tr key={invoice.id}>
                    <td>{startIndex + index + 1}</td>
                    <td>
                      <span className="order-id-badge">{invoice.displayOrderId}</span>
                    </td>

                    <td>
                      <div className="combined-agent-cell">
                        <span className="company-text"><strong>{invoice.companyName}</strong></span>
                        <div className="agent-sub-details">
                          <span className="agent-name-text">👤 {invoice.agentName}</span>
                          <span className="agent-phone-text">📞 {invoice.agentPhone}</span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <span className="cart-count-badge">
                        {invoice.cartDetails.length} Item(s)
                      </span>
                    </td>
                    <td>
                      <span className="outstanding-amount-text">
                        AED {invoice.invoiceAmount.toFixed(2)}
                      </span>
                    </td>
                    <td>
                      <span 
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          fontSize: '11px',
                          fontWeight: 'bold',
                          backgroundColor: invoice.verificationStatus === 'Verified' ? '#dcfce7' : '#fef3c7',
                          color: invoice.verificationStatus === 'Verified' ? '#166534' : '#92400e'
                        }}
                      >
                        {invoice.verificationStatus}
                      </span>
                    </td>

                    <td style={{ textAlign: 'center' }}>
                      <div className="action-buttons-wrapper">
                        <button
                          onClick={() => setSelectedInvoice(invoice)}
                          className="btn-icon-action btn-view-icon"
                          title="View / Edit Invoice Details"
                        >
                          👁️
                        </button>
                        <button
                          onClick={() => handleDownloadPDF(invoice)}
                          className="btn-icon-action btn-pdf-icon"
                          title="Download Invoice PDF"
                        >
                          📥
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* PAGINATION CONTROLS */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <button
              className="pagination-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage((prev) => prev - 1)}
            >
              Previous
            </button>

            <span className="pagination-info">
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong> ({filteredInvoices.length} Total)
            </span>

            <button
              className="pagination-btn"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage((prev) => prev + 1)}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* 🎯 EDIT & VERIFY INVOICE MODAL POPUP (CONNECTS DIRECTLY TO UPDATED CSS) */}
      {selectedInvoice && (
        <div className="modal-overlay" onClick={() => setSelectedInvoice(null)}>
          <div className="modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3 style={{ margin: 0, color: '#0f172a', fontSize: '1.25rem' }}>
                  Invoice Breakdown & Pricing Check ({selectedInvoice.displayOrderId})
                </h3>
                <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: '600', marginTop: '4px', display: 'block' }}>
                  🏢 {selectedInvoice.companyName} - Agent: {selectedInvoice.agentName}
                </span>
              </div>
              <button 
                onClick={() => setSelectedInvoice(null)} 
                style={{
                  background: '#f1f5f9',
                  border: 'none',
                  fontSize: '1.2rem',
                  color: '#64748b',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  cursor: 'pointer'
                }}
              >
                &times;
              </button>
            </div>

            <div className="modal-body">
              <div className="modal-info-grid">
                <div className="modal-info-card">
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#64748b', fontWeight: 'bold' }}>AGENT PHONE</span>
                  <span style={{ fontSize: '0.95rem', fontWeight: 'bold', color: '#0f172a' }}>{selectedInvoice.agentPhone}</span>
                </div>
                <div className="modal-info-card highlight-card">
                  <span style={{ display: 'block', fontSize: '0.75rem', color: '#dc2626', fontWeight: 'bold' }}>VERIFICATION STATUS</span>
                  <span style={{ fontSize: '1.05rem', fontWeight: '800', color: selectedInvoice.verificationStatus === 'Verified' ? '#16a34a' : '#dc2626' }}>
                    {selectedInvoice.verificationStatus}
                  </span>
                </div>
              </div>

              <h4 style={{ margin: '16px 0 10px 0', fontSize: '0.95rem', color: '#0f172a' }}>
                🛒 Cart Items Breakdown & Agent Pricing Check ({editingCart ? editingCart.length : 0} Items)
              </h4>

              {editingCart && editingCart.length > 0 ? (
                <>
                  <div className="modal-cart-table-wrapper">
                    <table>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left' }}>#</th>
                          <th style={{ textAlign: 'left' }}>Item Name</th>
                          <th style={{ textAlign: 'left' }}>Service Name</th>
                          <th style={{ textAlign: 'center' }}>Qty / Weight</th>
                          <th style={{ textAlign: 'right' }}>Unit Price (AED)</th>
                          <th style={{ textAlign: 'right' }}>Agent Amount (AED)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {editingCart.map((item, idx) => {
                          const isKg = item.pricingType === 'kg';
                          return (
                            <tr key={idx}>
                              <td>{idx + 1}</td>
                              <td>
                                <strong>{item.name || item.itemName || 'Item'}</strong>
                              </td>
                              <td style={{ color: '#475569' }}>
                                {item.serviceName || 'Wash'}
                              </td>
                              <td style={{ textAlign: 'center' }}>
                                <input
                                  type="number"
                                  step={isKg ? "0.1" : "1"}
                                  min="0"
                                  value={isKg ? (item.weight || 0) : (item.quantity || 1)}
                                  onChange={(e) => handleItemFieldChange(idx, isKg ? 'weight' : 'quantity', e.target.value)}
                                  style={{
                                    width: '70px',
                                    padding: '5px 6px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    textAlign: 'center'
                                  }}
                                />
                                <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '4px' }}>
                                  {isKg ? 'Kg' : 'Pcs'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.unitPrice || 0}
                                  onChange={(e) => handleItemFieldChange(idx, 'unitPrice', e.target.value)}
                                  style={{
                                    width: '85px',
                                    padding: '5px 6px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    textAlign: 'right'
                                  }}
                                />
                              </td>
                              <td style={{ textAlign: 'right' }}>
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={item.agentAmount || 0}
                                  onChange={(e) => handleItemFieldChange(idx, 'agentAmount', e.target.value)}
                                  style={{
                                    width: '95px',
                                    padding: '5px 6px',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: '6px',
                                    textAlign: 'right',
                                    fontWeight: 'bold',
                                    color: '#15803d'
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* 🎯 TOTAL AGENT AMOUNT SUMMARY BOX BELOW THE TABLE */}
                  <div 
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      backgroundColor: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      marginTop: '12px'
                    }}
                  >
                    <span style={{ fontSize: '0.9rem', fontWeight: 'bold', color: '#166534' }}>
                      Total Agent Amount ({editingCart.length} Items)
                    </span>
                    <span style={{ fontSize: '1.15rem', fontWeight: '800', color: '#15803d' }}>
                      AED {currentModalAgentTotal.toFixed(2)}
                    </span>
                  </div>
                </>
              ) : (
                <p style={{ color: '#64748b', fontSize: '0.85rem', fontStyle: 'italic' }}>No cart details recorded for this invoice.</p>
              )}
            </div>

            <div className="modal-footer">
              <button 
                onClick={handleVerifyAndApprove} 
                disabled={isVerifying}
                style={{
                  backgroundColor: '#2563eb',
                  color: '#ffffff',
                  border: 'none',
                  padding: '9px 18px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  cursor: isVerifying ? 'not-allowed' : 'pointer'
                }}
              >
                {isVerifying ? 'Verifying...' : '✔ Verify & Approve Price'}
              </button>
              <button 
                onClick={() => setSelectedInvoice(null)} 
                className="btn-modal-close"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentOutstanding;